import styled, { keyframes } from "styled-components";

const theme = {
  bg: "#1a1a1a",
  surface: "#2c2c2c",
  surfaceHover: "#333333",
  gold: "#B17D2A",
  goldHover: "#d49a3f",
  text: "#ffffff",
  textSecondary: "#aaaaaa",
  border: "#444444",
  danger: "#ff4444",
  dangerHover: "#cc0000",
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.9) translateY(-20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Container = styled.div`
  padding: 80px 40px;
  color: ${theme.text};
  max-width: 1000px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;

  h1 {
    font-size: 2rem;
    color: ${theme.gold};
    margin: 0;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

export const Card = styled.div`
  background: ${theme.surface};
  padding: 30px;
  border-radius: 16px;
  margin-bottom: 25px;
  border: 1px solid ${theme.border};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 1.25rem;
    color: ${theme.text};
    border-left: 4px solid ${theme.gold};
    padding-left: 15px;
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 25px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin-bottom: 10px;
    font-size: 0.9rem;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input,
  select {
    background: #1a1a1a;
    border: 1px solid ${theme.border};
    color: ${theme.text};
    padding: 12px 15px;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.3s ease;

    &:focus {
      outline: none;
      border-color: ${theme.gold};
    }
  }

  select {
    cursor: pointer;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
  margin-top: 10px;

  th {
    padding: 12px;
    text-align: left;
    color: ${theme.textSecondary};
    font-size: 0.85rem;
    text-transform: uppercase;
  }

  td {
    padding: 12px;
    background: rgba(0, 0, 0, 0.2);

    &:first-child {
      border-radius: 8px 0 0 8px;
    }
    &:last-child {
      border-radius: 0 8px 8px 0;
    }
  }

  input {
    background: transparent;
    border: 1px solid transparent;
    color: ${theme.text};
    padding: 8px;
    border-radius: 4px;
    width: 100%;
    font-size: 1rem;

    &:focus,
    &:hover {
      background: #1a1a1a;
      border-color: ${theme.border};
    }
  }
`;

export const SaveButton = styled.button`
  background: ${theme.gold};
  color: #fff;
  border: none;
  padding: 14px 28px;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(177, 125, 42, 0.2);
  min-width: 180px;

  &:hover:not(:disabled) {
    background: ${theme.goldHover};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(177, 125, 42, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const AddButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.gold};
  color: ${theme.gold};
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.gold};
    color: #fff;
  }
`;

export const DeleteButton = styled.button`
  background: rgba(255, 68, 68, 0.15);
  border: 1px solid transparent;
  color: ${theme.danger};
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.danger};
    color: #fff;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
`;

export const ModalContent = styled.div`
  background: ${theme.surface};
  padding: 40px;
  border-radius: 24px;
  width: 95%;
  max-width: 500px;
  border: 1px solid ${theme.border};
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  animation: ${scaleIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  h2 {
    margin: 0 0 10px 0;
    color: ${theme.gold};
  }

  p {
    color: ${theme.textSecondary};
    margin-bottom: 30px;
    font-size: 0.95rem;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
`;

export const EditIconButton = styled.button`
  background: rgba(177, 125, 42, 0.1);
  border: 1px solid ${theme.gold};
  color: ${theme.gold};
  width: 45px;
  height: 45px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.gold};
    color: #fff;
    transform: rotate(15deg);
  }
`;

export const LoadingSpinner = styled(spin)`
  animation: ${spin} 1s linear infinite;
  font-size: 1.2rem;
`;

export const SpinnerIcon = styled.div`
  display: inline-block;
  animation: ${spin} 1s linear infinite;
  line-height: 0;
`;

export const TabBar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  background: ${theme.surface};
  padding: 8px;
  border-radius: 12px;
  border: 1px solid ${theme.border};
  width: fit-content;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const TabButton = styled.button`
  background: ${(props) => (props.$active ? theme.gold : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : theme.textSecondary)};
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    color: ${(props) => (props.$active ? "#fff" : theme.text)};
    background: ${(props) => (props.$active ? theme.goldHover : theme.surfaceHover)};
  }

  @media (max-width: 600px) {
    flex: 1;
    padding: 12px 12px;
  }
`;

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) =>
    props.$active ? "rgba(46, 204, 113, 0.15)" : "rgba(255, 68, 68, 0.15)"};
  color: ${(props) => (props.$active ? "#2ecc71" : theme.danger)};
  border: 1px solid
    ${(props) => (props.$active ? "rgba(46, 204, 113, 0.4)" : "rgba(255, 68, 68, 0.4)")};
`;

export const ActionButtonsCell = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

export const IconButton = styled.button`
  background: rgba(177, 125, 42, 0.1);
  border: 1px solid ${theme.gold};
  color: ${theme.gold};
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.gold};
    color: #fff;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  color: #666;
`;
